import { PaginationParams, DataElement, FileUpload, Doctor } from "./common";
import { ChatContact, ChatTask } from "./chat.model";
import { stringList } from "aws-sdk/clients/datapipeline";
import { StreamingConfiguration } from "aws-sdk/clients/chime";
import { SharedService } from "../services/shared.service";
import { DateTime } from "aws-sdk/clients/devicefarm";
import { bool } from "aws-sdk/clients/signer";
import { Clinic } from "./clinic.model";
import { NumberLiteralType } from "typescript";
import { UiUser } from "./ui-user.model";
import { IntakePartyFlags } from "./intake-party.model";

export interface IntakeState {
  formId: number;
  takerUserId: number | null;
  takerName: string | null;
  party?: IntakePartyFlags | null;
}

export class Order {
  orderId: number;
  orderIdMmd: string;
  stageId: number;
  displayId: string;
  stageDescr: string;
  stageLongDescr: string;
  orderDate: Date;
  plantOrderDate: Date;
  curDueDate: Date;
  plantDueDate: Date;
  clinicDueDate: Date;
  orderPdfUrl: string;
  statusCode: string;
  statusDescr: string;
  intake: IntakeState | null = null;
  statusNote: string;
  statusCnt: number;
  clinicId: number;
  clinicDescr: string;
  doctorId: number;
  doctorFullName: string;
  plantId: number;
  plantDescr: string;
  orderTypeCode: string;
  orderTypeDescr: string;
  barcode: string;
  favoriteSw: boolean;
  unreadSw: boolean;
  totMsgCnt: number;
  unreadMsgCnt: number;
  editableParams: EditableParams;
  patientId: number;
  patientTz: string;
  patientFirstName: string;
  patientLastName: string;
  patientFullName: string;
  patientGender: string;
  patientBirthYear: number;
  orderDateFormatted: string;
  curDueDateFormatted: string;
  plantDueDateFormatted: string;
  clinicDueDateFormatted: string;
  orderLines: OrderLine[] = [];
  activityLog: OrderActivityLog[];
  files: OrderFile[] = [];
  filesRX: OrderFile[] = [];
  chatContacts: ChatContact[];
  stageRepeatSw: boolean;
  shade: string;
  urgencyPlantCode: string;
  urgencyPlantDescr: string;
  urgencyLabCode: string;
  urgencyLabDescr: string;
  chatTasks: ChatTask[] = [];
  tasksSw: boolean;
  origClinicDescr: string;
  origClinicId: number;
  checkedSw: boolean;
  alertDueDateChangeSw: boolean;
  origDueDateFormatted: string;
  origDueDate: Date;
  origDueDateForUser: string;
  origClinicDueDate: string;
  origPlantDueDate: string;
  dueDateChangeDate: string;
  dueDateChangeAccept: DueDateChangeAccept[] = [];
  ordTik: number;
  ordScanNum: string;
  totTasksCnt: number;
  openTasksCnt: number;
  orderTrackerGroup: string;
  notes: OrderNote[] = [];
  drProfile: DrProfile;
  leumitSw: boolean;
  inhouseSw: boolean;
  allowCreateRxSw: boolean;
  refundSw: boolean;
  orderEmails: OrderEmail[] = [];
  mmdClosedSw: boolean;
  agentCode: number;
  agentDescr: string;
  qcStatus: string;
  qcStatusUserName?: string;
  qcStageOptions: QCStageOpt[] = [];
  rxGist: RXGist;
  technician: string;

  get hasRx(): boolean {
    return this.filesRX.filter((f) => f.rxSw).length > 0;
  }

  get rxToDownload(): OrderFile[] {
    return this.filesRX.filter((f) => !f.lastDownloadTimestamp);
  }

  constructor() {
    this.editableParams = new EditableParams();
  }
}

export class QCStageOpt extends DataElement {
  enabledSw: boolean;
  primarySw: boolean;
  constructor(id: string = undefined, text: string = undefined) {
    super(id, text);
  }
}

export class DueDateChangeAccept {
  userId: number;
  userDescr: string;
  userAcceptDate: string;
}

export class OrderFile {
  fileId: string;
  url: string;
  fileName: string;
  fileType: string;
  uploadDate: Date;
  uploadDateFormatted: string;
  uploadStatus: FileUploadStatusEnum;
  file: FileUpload;
  rxSw: boolean;
  lastDownloadTimestamp: string;
  lastDownloadUserDescr: string;
  isVideo: boolean;
  mimeType: string;
  videoSrc: any;
  modelType: string;

  rxType: string;
  formId: number;
  createDate: Date;
  statusCode: string;

  constructor() { }
}

export class EditableParams {
  dueDateSw: boolean;
  custLinesSw: boolean;
  plantLinesSw: boolean;
  itemsSw: boolean;
}

export class OrderSearchParams extends PaginationParams {
  orderIdMmd?: string;
  clinicList?: DataElement[];
  doctorList?: DataElement[];
  rxItemList?: DataElement[];
  deliverer?: DataElement;
  taskOwnerList?: UiUser[];
  patientName?: string;
  orderFromDate?: Date;
  orderToDate?: Date;
  stage?: DataElement;
  urgency?: DataElement;
  orderId?: string;
  chatCode?: string;
  summaryCode?: string;
  favoriteSw?: boolean;
  tasksSw?: boolean;
  rxSw?: boolean;
  ordScanNum?: string;
  ordTik?: string;
  chatMsg?: DataElement;
  inhouseSw?: boolean;
  qcModeSw?: boolean;

  constructor() {
    super();
  }
}

export class OrderLine {
  custLineId: number;
  lineType: string;
  qty: number;
  teethList: string;
  item: OrderItem;
  selected: boolean;
  editableSw: boolean;
  newLineSw: boolean;
  itemCode: string;
  teethValidation: boolean = true;
  teethValidationMsg: string;
  seq: number;
  lineDiscount: number;
  lineTotal: number;
  lineUnitPrice: number;

  constructor(seq: number) {
    this.seq = seq;
  }

  get isEmptyRow(): boolean {
    return !this.custLineId && !this.item && !this.teethList && !this.qty;
  }
}

export class OrderActivityLog {
  activityId: number;
  activityType: string;
  timestamp: Date;
  activityDisplayText: string;
  stageLongDescr: string;
  versionId: number;
  updUserId: number;
  updUserDescr: string;
  updUserRole: string;
  timeFormatted: string;
  dateFormatted: string;

  constructor() { }
}

export class OrderEmail {
  id: number;
  status: string;
  recipients: string[] = [];
  crDate: Date;
  templateCode: string;
  templateDescr: string;
  subject: string;
  body: string;
  updUserDescr: string;
  updDate: Date;
  sendDate: Date;
  delayId: number;

  constructor() { }
}

export class OrderNote {
  noteId: number;
  createUser: number;
  createUserDescr: string;
  timestamp: Date;
  text: string;
}

export class SystemParamSubject {
  subjectCode: string;
  subjectDescr: string;
  min: number = 0;
  max: number;
  params: SystemParam[] = [];
}

export class SystemParam {
  code: string;
  descr: string;
  value: string;
  dirtySw: boolean;
}

export class DrProfile {
  profileId: number;
  doctor: DataElement;
  clinic: DataElement;
  activeSw: boolean;
  updDate: Date;
  pref: DrProfilePref[] = [];
  signDataUrl: string;
  signedBy: string;
  signDate: Date;
  createMode: boolean;
}

export class DrProfilePref {
  prefId: number;
  prefType: string;
  opt: RXOpt;
  optValue: RXOptValue;
  iconKey: string;
  techIssue: DataElement;
  noteLineType: string;
  note: string;
  presetNoteId: number;
  technician: DataElement;
  pgSw: boolean;
  pgSnoozeDate: Date;
}

export class DrProfileSearchParams extends PaginationParams {
  clinicId?: number;
  doctorId?: number;
  profileId?: number;

  constructor() {
    super();
  }
}

export class RXOpt extends DataElement {
  valueList: RXOptValue[] = [];
  lineType: string;
  valueType: string;
  displayType: string;
  selectedValue: RXOptValue;
  valueText: string;
  valueNum: number;
  valueBool: boolean;
  drPrefSw: boolean;
  min: number;
  max: number;
  required: boolean;
  drPrefDescr: string;

  constructor(id: string = undefined, text: string = undefined) {
    super(id, text);
  }

  resetValue() {
    this.selectedValue = null;
    this.valueBool = null;
    this.valueNum = null;
    this.valueText = null;
  }

  hasValue() {
    return (
      this.selectedValue ||
      this.valueText ||
      this.valueNum ||
      this.valueBool != null
    );
  }
}

export class RXOptValue extends DataElement {
  iconKey: string;

  constructor(id: string = undefined, text: string = undefined) {
    super(id, text);
  }
}

export class RXGist {
  lines: RXLine[] = [];
  rxOptions: RXOptGroup[] = [];
  implantTeeth?: RXTooth[] = [];
  approveDate?: Date;
}

export class RXForm {
  formId: number;
  updDate: Date;
  crDate: Date;
  updUser: string;
  intake: IntakeState | null = null;
  statusCode: string;
  statusDescr: string;
  canvasDataUrl: string;
  notes: RXNote[] = [];
  lines: RXLine[] = [];
  rxOptions: RXOptGroup[] = [];
  teethDataUrl?: string;
  extractTeeth?: RXTooth[] = [];
  implantTeeth?: RXTooth[] = [];
  pdf: RXPdf[] = [];
  allowChangeToDraft: boolean;
  // a signed LAB RX waits for Meimad's number, then its PDF is made by the server: no signing or reopening meanwhile
  mmdSyncPending = false;
  // a signing's reply: true when the server will make the PDF once Meimad's number arrives; absent from an older server
  pdfAfterMmd?: boolean;
  validationErrors: string[] = [];
  type: string;
  doctor: Doctor;
  patientId: number;
  patientDescr: string;
  publicId: string;
  rxDefaultLang: string;
  showOrderLines: boolean;
  orderLines: OrderLine[] = [];
  clinic?: Clinic;
}

export class RXPdf {
  lang: string;
  fileUrl: string;
}

export class RXNote {
  noteId?: number;
  drProfileId?: number;
  presetNoteId?: number;
  text?: string;
  // null for the dentist's and the lab's notes; IMPORT_DETAILS for what an imported case carried that the RX has no place for
  kind?: string | null;

  constructor() { }
}

export const RX_NOTE_IMPORT_DETAILS = "IMPORT_DETAILS";

// A note of the RX's free-text box: not from the notes bank or a doctor profile, and not the import's details.
export function isFreeRxNote(note: RXNote): boolean {
  return !note.drProfileId && !note.presetNoteId && note.kind !== RX_NOTE_IMPORT_DETAILS;
}

export class RXLine {
  lineId?: number;
  rxItem?: RXItem;
  teeth?: RXTooth[] = [];
  stage: DataElement;
  toothDisplayText: string;
  dirtySw?: boolean;
  qty: number;

  constructor() {
    this.teeth = SharedService.teethDef.map(
      (t) => new RXTooth(t.id, t.sortOrder, t.jaw)
    );
  }
}

export class RXTooth {
  id: number;
  sortOrder: number;
  jaw: number;
  dirtySw?: boolean;
  type?: string;
  splintedGroup?: number;
  implantType: RXOpt;
  implantCompany: RXOpt;
  implantDiameter: RXOpt;
  implantIndivSw: RXOpt;
  implantMultiUnitSw: RXOpt;
  extractSw: boolean;
  implantSw: boolean;
  toothSeq: number;

  get icon(): string {
    if (this.dirtySw) {
      return "assets/img/teeth/dirty/" + this.id + ".svg";
    } else if (this.type) {
      return "assets/img/teeth/dirty/" + this.id + ".svg";
    } else {
      return "assets/img/teeth/default/" + this.id + ".svg";
    }
  }

  get defaultIcon(): string {
    return "assets/img/teeth/default/" + this.id + ".svg";
  }

  get singleIcon(): string {
    if (this.dirtySw) {
      return "assets/img/teeth/selected/" + this.id + ".svg";
    } else return "assets/img/teeth/dirty/" + this.id + ".svg";
  }

  constructor(id: number, sortOrder: number, jaw: number) {
    this.id = id;
    this.sortOrder = sortOrder;
    this.jaw = jaw;
  }
}

export class OrderItem extends DataElement { }

export class RXItem extends DataElement {
  lineType: string;
  displayGroup: string;
  allowSplintedSw: boolean;
  allowSingleSw: boolean;
  allowJawSw: boolean;
  stages: DataElement[] = [];
  activeSw: boolean = true;
  mmdCodes: string[] = [];
  mmdCodesFilter: string;
  teethQtyType: string;
}

export class RXOptGroup {
  optGroup?: string;
  optGroupDescr?: string;
  options: RXOpt[] = [];
}

export class PresetNote {
  id?: number;
  updUser?: string;
  updDate?: DateTime;
  status?: string;
  statusDescr?: string;
  displayLabSw?: boolean;
  allItemsSw?: boolean;
  rxItems: RXItem[] = [];
  allDoctorsSw?: boolean;
  doctors: DataElement[] = [];
  itemCodes: string[] = [];
  doctorCodes: string[] = [];
  he?: string;
  en?: string;
  cn?: string;
}

export class PresetNoteSearchParams extends PaginationParams {
  noteText?: string;

  constructor() {
    super();
  }
}

export class CreateOrderData {
  orderId?: number;
  formId?: number;
}

export class OrderEmailContent {
  subject?: string;
  body?: string;
  recipients: string[] = [];
}

export class DrProfileWizardStepEnum {
  static RX_OPT_FIXED: string = "RX_OPT_FIXED";
  static RX_OPT_REMOVABLE: string = "RX_OPT_REMOVABLE";
  static AUTO_APPROVE: string = "AUTO_APPROVE";
  static PERSONAL_GUIDANCE: string = "PERSONAL_GUIDANCE";
  static NOTE: string = "NOTE";
  static SIGN: string = "SIGN";
}

export class SupplyTypesEnum {
  static SMALL_BAG: string = "SMALL_BAG";
  static LARGE_BAG: string = "LARGE_BAG";
  static CROWN_ORDER_FORM: string = "CROWN_ORDER_FORM";
  static DENTURE_ORDER_FORM: string = "DENTURE_ORDER_FORM";
  static DELIVERY: string = "DELIVERY";
  static BARCODE_ROLL: string = "BARCODE_ROLL";
  static OTHER: string = "OTHER";
}

export class UrgencyEnum {
  static HIGH: string = "HIGH";
  static LOW: string = "LOW";
  static CRITICAL: string = "CRITICAL";
  static MED: string = "MED";
}

export class FileUploadStatusEnum {
  static SUCCESS: string = "SUCCESS";
  static PROGRESS: string = "PROGRESS";
  static ERROR: string = "ERROR";
}

export class ToothTypeEnum {
  public static SPLINTED: string = "SPLINTED";
  public static SINGLE_UNIT: string = "SINGLE_UNIT";
}

export class QCStatusEnum {
  static UPCOMING_QC: string = "UPCOMING_QC";
  static PASSED_QC: string = "PASSED_QC";
  static FAILED_QC: string = "FAILED_QC";
  static PASSED_BILLING: string = "PASSED_BILLING";
  static WAYBILL_CREATED: string = "WAYBILL_CREATED";
  static QC_SKIPPED: string = "QC_SKIPPED";
}

